/* Live route summary while building — M5.8, and the M17.8 recalc guard.
 *
 * Pure and headless: the numbers the edit strip renders, plus the decision
 * that decides whether dragging a vertex must make the autorouter recompute.
 * Nothing here touches the DOM, the worker or the editor — the strip wires
 * these functions to those, which is what keeps the arithmetic testable.
 */

import { rangeM } from './chartpick.ts';

/** The speed a route is planned at when the boat is not actually making way. */
export const PLANNED_SPEED_KN = 5;
/** Below this a SOG reading is noise (or the boat is anchored), so it is not
 *  used for planning — the ETA arithmetic still produces a number, but it is
 *  a lie about a boat that is not closing on anything. */
export const MIN_SOG_KN = 0.5;

const KN = 1.94384;

/** Planned speed in knots: the boat's SOG when it is making way, else the
 *  planning default. SOG arrives in m/s; a route is planned in knots. */
export function plannedSpeedKn(sogMs: number | null | undefined): number {
  const sogKn = typeof sogMs === 'number' && Number.isFinite(sogMs) ? sogMs * KN : 0;
  return sogKn > MIN_SOG_KN ? sogKn : PLANNED_SPEED_KN;
}

export interface EteEta {
  /** Seconds to go — feed to fmtEte. */
  eteS: number;
  /** Arrival as an ISO string — feed to fmtEta. */
  etaIso: string;
}

/** ETE/ETA for a distance at the planned speed. Null for an empty route,
 *  where distance is not a number worth planning against. */
export function eteEtaFor(
  nm: number | null | undefined,
  sogMs: number | null | undefined,
  nowMs = Date.now()
): EteEta | null {
  if (typeof nm !== 'number' || !Number.isFinite(nm) || nm <= 0) return null;
  const eteS = (nm / plannedSpeedKn(sogMs)) * 3600;
  return { eteS, etaIso: new Date(nowMs + eteS * 1000).toISOString() };
}

/**
 * Whether dragging a vertex should make the autorouter re-solve.
 *
 * ═══ ANY POINT, NOT ONLY THE DESTINATION (14 Aug 2026) ═══
 *
 * This used to answer true only for the LAST point, on the reading that any
 * other vertex was a hand-edit. That is not how dragging a route reads: on an
 * autorouted draft the intermediate points are the ROUTER'S own work, not
 * decisions anybody made, so dragging one says "go through here" rather than
 * "freeze the router's guess". ChartPage solves start→dragged→end and bends
 * the route through the point, live, while the finger is down.
 *
 * A hand-drawn route still never re-routes: nothing is being solved, and
 * re-solving would discard a line somebody drew on purpose.
 */
export function shouldRecalcOnDrag(
  autorouted: boolean,
  draggedIndex: number,
  pointCount: number
): boolean {
  return autorouted && pointCount >= 2 && draggedIndex >= 0 && draggedIndex < pointCount;
}

/**
 * How far a route is, in metres, along its legs.
 *
 * ═══ ALONG THE LEGS, WHICH IS THE ONLY HONEST ANSWER ═══
 *
 * Not start-to-end. A route that dog-legs around an island is exactly as long
 * as the water you have to cover, and the straight line between its ends is a
 * number that would make every route look shorter than the passage it
 * describes — most misleadingly for the routes that bend the most, which are
 * the ones somebody asked the router to solve BECAUSE they bend.
 *
 * Fewer than two points is not a short route, it is not a route — null, so a
 * caller shows a dash rather than a confident 0.0 nm.
 */
export function routeDistanceM(pts: readonly { lat: number; lon: number }[]): number | null {
  if (pts.length < 2) return null;
  let m = 0;
  for (let i = 1; i < pts.length; i++) m += rangeM(pts[i - 1]!, pts[i]!);
  return m;
}
